import { useMemo, type ReactNode } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { fontFamily, space, textStyle } from "@passfirst/ui";

import { type Colors, useColors } from "../theme";

interface Props {
  title?: string;
  /** Left slot — typically a back arrow or empty spacer. */
  left?: ReactNode;
  /** Right slot — typically a badge or empty spacer. */
  right?: ReactNode;
  /** Convenience: render a "‹ Back" button on the left calling this. */
  onBack?: () => void;
}

// Reusable screen appbar. State picker, paywall, diagnostic, home —
// every "inside" screen has the same chrome shape (left slot · centered
// title · right slot). Spacers default to 48px to balance the title.
export function ScreenAppbar({
  title,
  left,
  right,
  onBack,
}: Props): JSX.Element {
  const colors = useColors();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const leftContent =
    left ??
    (onBack ? (
      <Pressable
        onPress={onBack}
        accessibilityRole="button"
        accessibilityLabel="Back"
        style={styles.back}
      >
        <Text style={styles.backText}>‹ Back</Text>
      </Pressable>
    ) : (
      <View style={styles.spacer} />
    ));
  return (
    // Offset the appbar below the status bar / notch. Keeps the existing
    // space[4] gap beneath the inset.
    <View style={[styles.appbar, { paddingTop: insets.top + space[4] }]}>
      {leftContent}
      {title ? (
        <Text
          style={styles.title}
          numberOfLines={1}
          ellipsizeMode="tail"
          accessibilityRole="header"
        >
          {title}
        </Text>
      ) : (
        <View />
      )}
      {right ?? <View style={styles.spacer} />}
    </View>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    appbar: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: space[5],
      paddingTop: space[4],
      paddingBottom: space[3],
    },
    title: {
      fontFamily: fontFamily.semiBold,
      fontSize: 14,
      color: colors.textPrimary,
      flex: 1,
      textAlign: "center",
    },
    spacer: { width: 48 },
    back: { padding: space[2], marginLeft: -space[2] },
    backText: { ...textStyle.body, color: colors.textSecondary },
  });
}
